import ChartPoint from './ChartPoint';
import ChartBar from './ChartBar';
import ChartStep from './ChartStep';
import ChartRange from './ChartRange';
import type { VNode } from 'vue';

/** Цвета слоёв. Проп называется color, как у сегментов VProgressBar */
export const CHART_COLORS = ['yellow', 'blue', 'green', 'purple', 'red'] as const;

export type ChartColor = typeof CHART_COLORS[number];

/** Имена тегов-слоёв; по ним VChart разбирает слот */
const LAYER_TAGS = {
  points: 'VChartPoints',
  bars:   'VChartBars',
  steps:  'VChartStepLine',
  ranges: 'VChartRanges',
} as const;

/**
 * Слой графика, разобранный из тега <VChartPoints>, <VChartBars>, <VChartStepLine> или <VChartRanges>.
 * Заполнен ровно один из массивов points / bars / steps / ranges — по kind.
 */
class ChartLayer {
  readonly kind: 'points' | 'bars' | 'steps' | 'ranges';

  /** Подпись в легенде; она же ключ скрытия слоя кликом по легенде */
  readonly label: string;

  readonly color: ChartColor;

  /** Точки кольцом без заливки (только для points) */
  readonly isHollow: boolean;

  /** Заливка под ступенями до низа графика (только для steps) */
  readonly isFilled: boolean;

  /** Ступени пунктиром (только для steps) */
  readonly isDashed: boolean;

  readonly points: ChartPoint[];

  readonly bars: ChartBar[];

  readonly steps: ChartStep[];

  readonly ranges: ChartRange[];

  constructor(
    kind: 'points' | 'bars' | 'steps' | 'ranges',
    label: string,
    color: ChartColor,
    isHollow: boolean,
    isFilled: boolean,
    isDashed: boolean,
    points: ChartPoint[],
    bars: ChartBar[],
    steps: ChartStep[],
    ranges: ChartRange[],
  ) {
    this.kind = kind;
    this.label = label;
    this.color = color;
    this.isHollow = isHollow;
    this.isFilled = isFilled;
    this.isDashed = isDashed;
    this.points = points;
    this.bars = bars;
    this.steps = steps;
    this.ranges = ranges;
  }

  /**
   * Слои из vnode слота в порядке тегов. Порядок тегов — порядок отрисовки
   * внутри своего вида: столбики всегда под линиями, отрезки над линиями, точки всегда сверху.
   * Вызывается на каждом рендере, поэтому Vue сам отслеживает данные слоёв и v-if на тегах.
   */
  static fromSlot(vnodes: VNode[]): ChartLayer[] {
    const result: ChartLayer[] = [];

    ChartLayer.collect(vnodes, result);

    return result;
  }

  /** Обход vnode; рекурсия — для v-for и v-if, они приходят Fragment'ом */
  private static collect(vnodes: VNode[], result: ChartLayer[]): void {
    for (const vnode of vnodes) {
      if (!vnode.type) continue;

      const name = (vnode.type as { name?: string }).name;
      const props = (vnode.props || {}) as Record<string, unknown>;
      const label = props.label as string;
      const color = props.color as ChartColor;

      if (name === LAYER_TAGS.points) {
        // Булев проп без значения (<VChartPoints hollow>) в vnode приходит пустой строкой
        const isHollow = props.hollow === true || props.hollow === '';

        result.push(new ChartLayer('points', label, color, isHollow, false, false, (props.points as ChartPoint[]) ?? [], [], [], []));
      } else if (name === LAYER_TAGS.bars) {
        result.push(new ChartLayer('bars', label, color, false, false, false, [], (props.bars as ChartBar[]) ?? [], [], []));
      } else if (name === LAYER_TAGS.steps) {
        const isFilled = props.fill === true || props.fill === '';
        const isDashed = props.dashed === true || props.dashed === '';

        result.push(new ChartLayer('steps', label, color, false, isFilled, isDashed, [], [], (props.steps as ChartStep[]) ?? [], []));
      } else if (name === LAYER_TAGS.ranges) {
        result.push(new ChartLayer('ranges', label, color, false, false, false, [], [], [], (props.ranges as ChartRange[]) ?? []));
      }

      if (Array.isArray(vnode.children)) {
        ChartLayer.collect(vnode.children as VNode[], result);
      }
    }
  }
}

export default ChartLayer;
